import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/fulfillment-methods',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    _: [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    _base_domain_: [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/fulfillment-methods/v1',
        destPath: '/v1',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/fulfillment-methods',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_fulfillment-methods';

/**
 * Creates a new fulfillment method.
 *
 * >**Note:** `fulfillmentMethod.availability.time_zone` uses the time zone specified in the [`language and regions`](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fsettings/language-and-region) settings in the dashboard, regardless of the value provided.
 */
export function createFulfillmentMethod(
  payload: object
): RequestOptionsFactory<any> {
  function __createFulfillmentMethod({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'fulfillmentMethod.createdDate' },
          { path: 'fulfillmentMethod.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'fulfillmentMethod.pickupOptions.address.geocode.latitude' },
          { path: 'fulfillmentMethod.pickupOptions.address.geocode.longitude' },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
          },
          { path: 'fulfillmentMethod.dineInOptions.address.geocode.latitude' },
          { path: 'fulfillmentMethod.dineInOptions.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.CreateFulfillmentMethod',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethod.createdDate' },
              { path: 'fulfillmentMethod.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createFulfillmentMethod;
}

/** Create multiple fulfillment methods at once. */
export function bulkCreateFulfillmentMethods(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateFulfillmentMethods({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'fulfillmentMethods.createdDate' },
          { path: 'fulfillmentMethods.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'fulfillmentMethods.pickupOptions.address.geocode.latitude' },
          {
            path: 'fulfillmentMethods.pickupOptions.address.geocode.longitude',
          },
          {
            path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
          },
          {
            path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
          },
          {
            path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
          },
          {
            path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
          },
          { path: 'fulfillmentMethods.dineInOptions.address.geocode.latitude' },
          {
            path: 'fulfillmentMethods.dineInOptions.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.BulkCreateFulfillmentMethods',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/bulk/fulfillment-methods/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.fulfillmentMethod.createdDate' },
              { path: 'results.fulfillmentMethod.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.fulfillmentMethod.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'results.fulfillmentMethod.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'results.fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'results.fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'results.fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'results.fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'results.fulfillmentMethod.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'results.fulfillmentMethod.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateFulfillmentMethods;
}

/** Retrieves a fulfillment method. */
export function getFulfillmentMethod(
  payload: object
): RequestOptionsFactory<any> {
  function __getFulfillmentMethod({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetFulfillmentMethod',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/{fulfillmentMethodId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethod.createdDate' },
              { path: 'fulfillmentMethod.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getFulfillmentMethod;
}

/**
 * Updates a fulfillment method.
 *
 * Each time the fulfillment method is updated, its revision increments by 1. The existing revision must be included when updating the fulfillment method. This ensures you're working with the latest fulfillment method information, and it prevents unintended overwrites.
 */
export function updateFulfillmentMethod(
  payload: object
): RequestOptionsFactory<any> {
  function __updateFulfillmentMethod({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'fulfillmentMethod.createdDate' },
          { path: 'fulfillmentMethod.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'fulfillmentMethod.pickupOptions.address.geocode.latitude' },
          { path: 'fulfillmentMethod.pickupOptions.address.geocode.longitude' },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
          },
          {
            path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
          },
          { path: 'fulfillmentMethod.dineInOptions.address.geocode.latitude' },
          { path: 'fulfillmentMethod.dineInOptions.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.UpdateFulfillmentMethod',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/{fulfillmentMethod.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethod.createdDate' },
              { path: 'fulfillmentMethod.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethod.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethod.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateFulfillmentMethod;
}

/** Deletes a fulfillment method. */
export function deleteFulfillmentMethod(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteFulfillmentMethod({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.DeleteFulfillmentMethod',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/{fulfillmentMethodId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteFulfillmentMethod;
}

/**
 * Creates a query to retrieve a list of fulfillment methods.
 *
 * The `queryFulfillmentMethods()` function builds a query to retrieve a list of fulfillment methods and returns a `FulfillmentMethodsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/fulfillment-methods/fulfillment-methods-query-builder/find) function.
 *
 * You can refine the query by chaining `FulfillmentMethodsQueryBuilder` functions onto the query. `FulfillmentMethodsQueryBuilder` functions enable you to filter, sort, and control the results that `queryFulfillmentMethods()` returns.
 *
 * `queryFulfillmentMethods()` runs with the following `FulfillmentMethodsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(50)`](/fulfillment-methods/fulfillment-methods-query-builder/limit)
 * * [`ascending('entityId')`](/fulfillment-methods/fulfillment-methods-query-builder/ascending)
 *
 * The following `FulfillmentMethodsQueryBuilder` functions are supported for `queryFulfillmentMethods()`. For a full description of the fulfillment method object, see the object returned for the [`items`](/fulfillment-methods/fulfillment-methods-query-result/items) property in `FulfillmentMethodsQueryResult`.
 */
export function queryFulfillmentMethods(
  payload: object
): RequestOptionsFactory<any> {
  function __queryFulfillmentMethods({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'projectionMask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.QueryFulfillmentMethods',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethods.createdDate' },
              { path: 'fulfillmentMethods.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryFulfillmentMethods;
}

/** Retrieves a list of up to 100 fulfillment methods. */
export function listFulfillmentMethods(
  payload: object
): RequestOptionsFactory<any> {
  function __listFulfillmentMethods({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'address.geocode.latitude' },
          { path: 'address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.ListFulfillmentMethods',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethods.createdDate' },
              { path: 'fulfillmentMethods.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listFulfillmentMethods;
}

/**
 * Retrieves a list of up to 100 fulfillment methods available for a given address.
 *
 * The response will only include:
 * - Non-delivery fulfillment methods.
 * - Delivery fulfillment methods that are available to the given address according to their delivery areas.
 */
export function listAvailableFulfillmentMethodsForAddress(
  payload: object
): RequestOptionsFactory<any> {
  function __listAvailableFulfillmentMethodsForAddress({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'address.geocode.latitude' },
          { path: 'address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.ListAvailableFulfillmentMethodsForAddress',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/available-for-address',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentMethods.createdDate' },
              { path: 'fulfillmentMethods.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress.geocode.longitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
              },
              {
                path: 'fulfillmentMethods.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentMethods.dineInOptions.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAvailableFulfillmentMethodsForAddress;
}

/**
 * Retrieves the accumulated availability of all fulfillment methods.
 * @deprecated It has been replaced with wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetAggregatedMethodAvailability(), and will be removed on 2024-09-29.
 */
export function getAccumulatedFulfillmentMethodsAvailability(
  payload: object
): RequestOptionsFactory<any> {
  function __getAccumulatedFulfillmentMethodsAvailability({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetAccumulatedFulfillmentMethodsAvailability',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/accumulated-availability',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getAccumulatedFulfillmentMethodsAvailability;
}

/**
 * Retrieves the combined availability of a list of fulfillment methods.
 *
 * The combined availability is a list of times during which one or more of the given fulfillment methods is available, and the types of those fulfillment methods.
 * @deprecated It has been replaced with wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetAggregatedMethodAvailability(), and will be removed on 2025-02-10.
 */
export function getCombinedMethodAvailability(
  payload: object
): RequestOptionsFactory<any> {
  function __getCombinedMethodAvailability({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetCombinedMethodAvailability',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/combined-availability',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getCombinedMethodAvailability;
}

/**
 * Retrieves the aggregated availability of a list of fulfillment methods.
 *
 * The aggregated availability is a list of times during which one or more of the given fulfillment methods is available, and the types of those fulfillment methods.
 */
export function getAggregatedMethodAvailability(
  payload: object
): RequestOptionsFactory<any> {
  function __getAggregatedMethodAvailability({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.GetAggregatedMethodAvailability',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/fulfillment-methods/aggregated-availability',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getAggregatedMethodAvailability;
}

/**
 * Synchronously update tags on multiple fulfillment methods.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateFulfillmentMethodTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateFulfillmentMethodTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.BulkUpdateFulfillmentMethodTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/bulk/fulfillment-methods/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateFulfillmentMethodTags;
}

/**
 * Asynchronously update tags on multiple fulfillment methods according to the specified filter.
 * If a filter isn't specified, this method updates all fulfillment methods.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateFulfillmentMethodTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateFulfillmentMethodTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.fulfillment_method',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.fulfillment.v1.FulfillmentMethodsService.BulkUpdateFulfillmentMethodTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsFulfillmentV1FulfillmentMethodsServiceUrl({
        protoPath: '/v1/bulk/fulfillment-methods/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateFulfillmentMethodTagsByFilter;
}
